/**
 * The view transform (TD §11): the whole map fits the canvas at zoom 1; zoom goes up to 8×; (cx, cy) is the map
 * point at the canvas centre. Screen units are CSS pixels.
 */
import type { Pt } from '../../shared/types';

/** Map ↔ screen for one canvas size. */
export interface Transform {
  /** Canvas size in CSS pixels. */
  w: number;
  h: number;
  /** Screen pixels per metre. */
  k: number;
  /** Screen position of map (0, 0). */
  ox: number;
  oy: number;
}

/** Smallest and largest zoom. */
export const ZOOM_MIN = 1;
export const ZOOM_MAX = 8;

/** The transform for a canvas of w×h showing a map of `size` metres at `zoom`, centred on (cx, cy). */
export function makeTransform(w: number, h: number, size: number, zoom: number, cx: number, cy: number): Transform {
  const k = (Math.min(w, h) / size) * zoom * 0.98;
  return { w, h, k, ox: w / 2 - cx * k, oy: h / 2 - cy * k };
}

/** Screen point of a map point. */
export function toScreen(t: Transform, x: number, y: number): Pt {
  return [t.ox + x * t.k, t.oy + y * t.k];
}

/** Map point of a screen point. */
export function toMap(t: Transform, sx: number, sy: number): Pt {
  return [(sx - t.ox) / t.k, (sy - t.oy) / t.k];
}

/** The view after zooming by factor f around screen point (sx, sy), keeping the map point under it in place. */
export function zoomAround(t: Transform, size: number, zoom: number, f: number, sx: number, sy: number): { zoom: number; cx: number; cy: number } {
  const nz = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom * f));
  const [mx, my] = toMap(t, sx, sy);
  const nk = t.k * (nz / zoom);
  // keep (mx, my) at (sx, sy): sx = w/2 + (mx - cx) * nk
  return { zoom: nz, cx: mx - (sx - t.w / 2) / nk, cy: my - (sy - t.h / 2) / nk };
}

/** Grid spacing in metres for a map size (the prototype's rule). */
export function gridStep(size: number): number {
  return size <= 500 ? 50 : 100;
}

/** Spreadsheet-style column name: 0 → A, 25 → Z, 26 → AA. */
export function colName(i: number): string {
  let s = '';
  let n = i + 1;
  while (n > 0) {
    const m = (n - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

/** Grid square of a map point, e.g. "C4". */
export function gridRef(size: number, x: number, y: number): string {
  const g = gridStep(size), c = (v: number) => Math.min(Math.max(v, 0), size - 0.001);
  return colName(Math.floor(c(x) / g)) + String(Math.floor(c(y) / g) + 1);
}
